import PropTypes from 'prop-types';
import { Button, Inline, breakpoints } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { X } from '@phosphor-icons/react';
import Media from 'react-media';
import { tripTypes } from '../../../constants/tripTypesConstants';
import { screenReadersOnlyStyles } from '../../../utils/styleUtils';
import {
  DEPARTURE_DATE,
  DESTINATION,
  MULTI_BOUND,
  ORIGIN,
  SINGLE_BOUND,
} from '../constants/formFields';
import Dates from '../containers/DatesContainer';
import OriginAndDestination from './OriginAndDestination';

const removeButtonStyles = css`
  margin-block-start: 21px;

  @media (min-width: ${breakpoints._768}) {
    height: 48px;
    padding: 0;
    width: 48px;
  }
`;

const emptyButtonBoxStyles = css`
  height: 48px;
  width: 48px;
`;

const removeTripIconStyles = css`
  height: 1.5rem;
  width: 1.5rem;
`;

const getFieldName = (fieldName, selectedTripType, index) =>
  selectedTripType !== tripTypes.MULTI_STOP
    ? `${SINGLE_BOUND}.${fieldName}`
    : `${MULTI_BOUND}[${index}].${fieldName}`;

const Bound = ({
  boundIndex,
  dateFormat,
  daysFromTodayForValidDepartureDate,
  form,
  hasRemoveButtons,
  languageCode,
  numberOfBounds,
  onRemove,
  selectedTripType,
  setAreOriginAndDestinationFilled,
}) => {
  const { t } = useTranslation();

  const getName = (fieldName) => getFieldName(fieldName, selectedTripType, boundIndex);
  const isRemovableTrip = boundIndex > 1;

  return (
    <div data-testid="bound">
      {selectedTripType !== tripTypes.MULTI_STOP ? (
        <Inline
          alignY="bottom"
          collapseBelow="768px"
          spacing={{
            [breakpoints._0]: 24,
            [breakpoints._768]: 0,
          }}
        >
          <OriginAndDestination
            boundIndex={boundIndex}
            destinationName={getName(DESTINATION)}
            form={form}
            originName={getName(ORIGIN)}
            selectedTripType={selectedTripType}
            setAreOriginAndDestinationFilled={setAreOriginAndDestinationFilled}
          />
        </Inline>
      ) : (
        <Inline collapseBelow="768px" spacing={24}>
          <OriginAndDestination
            boundIndex={boundIndex}
            destinationName={getName(DESTINATION)}
            form={form}
            originName={getName(ORIGIN)}
            selectedTripType={selectedTripType}
          />
          <Dates
            boundIndex={boundIndex}
            dateFormat={dateFormat}
            daysFromTodayForValidDepartureDate={daysFromTodayForValidDepartureDate}
            departureDateName={getName(DEPARTURE_DATE)}
            form={form}
            languageCode={languageCode}
            numberOfBounds={numberOfBounds}
            selectedTripType={selectedTripType}
          />
          <Media query={`(min-width: ${breakpoints._768})`}>
            {(matches) =>
              matches && (
                <>
                  {isRemovableTrip ? (
                    <Button
                      className={removeButtonStyles}
                      data-testid={`searchForm-removeTrip-${boundIndex}-non-mobile-button`}
                      onClick={() => {
                        onRemove(boundIndex);
                      }}
                    >
                      <span className={screenReadersOnlyStyles}>
                        {t('Searchform.RemoveTrip.Label')}
                      </span>
                      <X
                        aria-hidden
                        className={removeTripIconStyles}
                        color="currentColor"
                        weight="regular"
                      />
                    </Button>
                  ) : (
                    <>{hasRemoveButtons && <div className={emptyButtonBoxStyles} />}</>
                  )}
                </>
              )
            }
          </Media>
        </Inline>
      )}
    </div>
  );
};

Bound.propTypes = {
  boundIndex: PropTypes.number,
  dateFormat: PropTypes.string,
  daysFromTodayForValidDepartureDate: PropTypes.number,
  form: PropTypes.string,
  formConstraints: PropTypes.shape({
    passengers: PropTypes.shape({
      isDisabled: PropTypes.bool,
      maxNumberOfTravelers: PropTypes.number,
    }),
  }),
  hasRemoveButtons: PropTypes.bool,
  languageCode: PropTypes.string,
  numberOfBounds: PropTypes.number,

  onRemove: PropTypes.func,
  selectedTripType: PropTypes.string,
  setAreOriginAndDestinationFilled: PropTypes.func,
};

export default Bound;
